import { useCallback, useEffect, useRef, useState } from 'react'
import { errorMessage } from '@/lib/errors'
import { conflictVersion } from './api'

/**
 * Autosave com fila única (RN-PLA-24): grava 1,5 s depois da última tecla ou ao sair do campo;
 * um `PUT` por vez, e o que muda durante um `PUT` sai no seguinte. Trava otimista: o 409 relê o
 * registro uma vez e reaplica o que estava na tela; um segundo 409 para e mostra a causa. O que
 * falhou fica guardado para "tentar de novo".
 */

export type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'failed'

export interface AutosaveOptions<I extends object> {
  /** A versão que a tela tem do registro (a trava). */
  version: number
  /** Grava o que mudou com a versão da tela; devolve a versão nova. */
  save: (input: I, version: number) => Promise<{ version: number }>
  /** Relê o registro depois de um 409 e devolve a versão atual. */
  reload: () => Promise<number>
  delayMs?: number
}

export interface Autosave<I extends object> {
  /** Guarda o que mudou e agenda a gravação. */
  change(patch: Partial<I>): void
  /** Grava agora o que estiver pendente (ao sair do campo). */
  flush(): void
  /** Tenta de novo o que falhou. */
  retry(): void
  status: AutosaveStatus
  /** A causa da falha, quando `status` é `failed`. */
  error: string | null
  /** Há algo digitado que ainda não foi gravado. */
  dirty: boolean
}

export function useAutosave<I extends object>({
  version,
  save,
  reload,
  delayMs = 1500,
}: AutosaveOptions<I>): Autosave<I> {
  const pending = useRef<Partial<I> | null>(null)
  const inFlight = useRef(false)
  const conflictRetried = useRef(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const versionRef = useRef(version)
  const saveRef = useRef(save)
  const reloadRef = useRef(reload)
  const [status, setStatus] = useState<AutosaveStatus>('idle')
  const [error, setError] = useState<string | null>(null)
  const [dirty, setDirty] = useState(false)

  // A versão que a tela recebe de fora (a ficha recarregada) só vale se for mais nova; as funções
  // são sempre as da última renderização.
  useEffect(() => {
    if (version > versionRef.current) versionRef.current = version
  }, [version])
  useEffect(() => {
    saveRef.current = save
    reloadRef.current = reload
  }, [save, reload])

  const drain = useCallback(async () => {
    if (inFlight.current) return
    inFlight.current = true
    setStatus('saving')
    try {
      while (pending.current && Object.keys(pending.current).length > 0) {
        const patch = pending.current
        pending.current = null
        try {
          const result = await saveRef.current(patch as I, versionRef.current)
          versionRef.current = Math.max(versionRef.current, result.version)
          conflictRetried.current = false
        } catch (failure) {
          const current = conflictVersion(failure)
          if (current !== null && !conflictRetried.current) {
            // Alterado em outra tela: relê uma vez e reaplica o que estava digitado.
            conflictRetried.current = true
            try {
              versionRef.current = await reloadRef.current()
            } catch {
              versionRef.current = current
            }
            pending.current = { ...patch, ...(pending.current ?? {}) }
            continue
          }
          conflictRetried.current = false
          pending.current = { ...patch, ...(pending.current ?? {}) }
          setError(errorMessage(failure))
          setStatus('failed')
          return
        }
      }
      setError(null)
      setDirty(false)
      setStatus('saved')
    } finally {
      inFlight.current = false
    }
  }, [])

  const flush = useCallback(() => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = null
    void drain()
  }, [drain])

  const change = useCallback(
    (patch: Partial<I>) => {
      pending.current = { ...(pending.current ?? {}), ...patch }
      setDirty(true)
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(flush, delayMs)
    },
    [delayMs, flush],
  )

  // Ao sair da tela, o que ainda estava na fila sai (a guarda de saída já perguntou antes).
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current)
      if (pending.current && Object.keys(pending.current).length > 0 && !inFlight.current) {
        const patch = pending.current
        pending.current = null
        void saveRef.current(patch as I, versionRef.current).catch(() => {})
      }
    },
    [],
  )

  return { change, flush, retry: flush, status, error, dirty }
}
